import React, { useEffect, useState, useMemo } from 'react'
import { Organization } from 'interfaces/organization'
import CurrentSubscriptionItems from './current-subscription-items'
import PaymentMethodDisplay from './payment-method-display'
import FreeTrialDays from './free-trial-days'
import MetadataManager from './metadata-manager'
import DurationSection from './duration-section'
import { useOrgSubscriptionItems } from 'hooks/use-org-subscription-items'
import { usePaymentMethods } from 'hooks/use-payment-methods'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'
import useGetOrganizationCustomer from 'hooks/use-get-organization-customer'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_KEY } from 'constants/env'
import AddPaymentMethodModal from './add-payment-method-modal'

interface SubscriptionConfigurationProps {
  org: Organization
  onSubscriptionItemsChange?: (items: any[]) => void
  onResetBillingIntervalChange?: (reset: boolean) => void
  onProrationSettingsChange?: (settings: {
    enabled: boolean
    behavior: 'next_invoice' | 'immediately' | 'none'
  }) => void
  onOriginalSubscriptionItemsChange?: (items: any[]) => void
  onTrialDaysChange?: (trialDays: number | null) => void
  onMetadataChange?: (metadata: Array<{ key: string; value: string }>) => void
  onPaymentMethodChange?: (paymentMethodId: string | null) => void
  onPaymentDueDaysChange?: (days: number) => void
  onCouponsChange?: (coupons: any[]) => void
  invoicePreview?: any
}

const SubscriptionConfiguration: React.FC<SubscriptionConfigurationProps> = ({
  org,
  onSubscriptionItemsChange,
  onResetBillingIntervalChange,
  onProrationSettingsChange,
  onOriginalSubscriptionItemsChange,
  onTrialDaysChange,
  onMetadataChange,
  onPaymentMethodChange,
  onPaymentDueDaysChange,
  onCouponsChange,
  invoicePreview,
}) => {
  const STRIPE_TEXT_PRIMARY = '#32325d'
  const STRIPE_TEXT_SECONDARY = '#6b7c93'

  const { originalSubscriptionItems } = useOrgSubscriptionItems({
    orgId: org.id,
  })

  const {
    data: paymentMethods,
    isLoading: paymentMethodLoading,
    error: paymentMethodError,
  } = usePaymentMethods({
    organizationId: org.id,
  })

  const { data: customerData, isLoading: customerLoading } =
    useGetOrganizationCustomer({
      organizationId: org.id,
      enabled: !!org.id,
    })

  const { data: internalAdmins } = useGetInternalAdmins()

  const getNameById = (id: number | string) => {
    if (!internalAdmins) {
      return ''
    }

    const admin = internalAdmins.find(
      (admin) => admin.id.toString() === id.toString()
    )

    const name = admin?.name || ''
    return name
  }

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null)
  const [hasInitiallySelected, setHasInitiallySelected] = useState(false)
  const [showAddCardModal, setShowAddCardModal] = useState(false)
  const [trialDays, setTrialDays] = useState<number | null>(null)
  const [paymentDueDays, setPaymentDueDays] = useState<number>(15)
  const [metadata, setMetadata] = useState<
    Array<{ key: string; value: string }>
  >([
    {
      key: 'sale_rep_id',
      value: org.organization_subscription?.sale_rep_id?.toString() || '',
    },
    { key: 'sale_rep_name', value: '' }, // Will be updated when internal admins load
    { key: 'hubspot_deal_link', value: org.internal_crm_url || '' },
  ])

  useEffect(() => {
    if (internalAdmins && org.organization_subscription?.sale_rep_id) {
      const repName = getNameById(org.organization_subscription.sale_rep_id)

      setMetadata((prev) => {
        const updated = prev.map((item) =>
          item.key === 'sale_rep_name' ? { ...item, value: repName } : item
        )
        return updated
      })
    }
  }, [internalAdmins, org.organization_subscription?.sale_rep_id])

  const stripePromise = loadStripe(STRIPE_KEY || '')

  useEffect(() => {
    if (originalSubscriptionItems) {
      onOriginalSubscriptionItemsChange?.(originalSubscriptionItems)
    }
  }, [originalSubscriptionItems, onOriginalSubscriptionItemsChange])

  useEffect(() => {
    onTrialDaysChange?.(trialDays)
  }, [trialDays, onTrialDaysChange])

  useEffect(() => {
    onMetadataChange?.(metadata)
  }, [metadata, onMetadataChange])

  const sortedPaymentOptions = useMemo(() => {
    const options = []

    if (paymentMethods && paymentMethods.length > 0) {
      const sortedCards = [...paymentMethods].sort((a, b) => {
        if (a.default && !b.default) return -1
        if (!a.default && b.default) return 1
        return 0
      })
      options.push(...sortedCards.map((pm) => ({ id: pm.id, type: 'card' })))
    }

    options.push({ id: 'email_invoice_option', type: 'email' })

    return options
  }, [paymentMethods])

  useEffect(() => {
    if (paymentMethodLoading) {
      setHasInitiallySelected(false)
    }
  }, [paymentMethodLoading])

  useEffect(() => {
    if (
      !paymentMethodLoading &&
      sortedPaymentOptions.length > 0 &&
      !hasInitiallySelected
    ) {
      const firstOption = sortedPaymentOptions[0].id
      setSelectedPaymentMethodId(firstOption)
      setHasInitiallySelected(true)
    }
  }, [sortedPaymentOptions, paymentMethodLoading, hasInitiallySelected])

  useEffect(() => {
    onPaymentMethodChange?.(selectedPaymentMethodId)
  }, [selectedPaymentMethodId, onPaymentMethodChange])

  useEffect(() => {
    onPaymentDueDaysChange?.(paymentDueDays)
  }, [paymentDueDays, onPaymentDueDaysChange])

  return (
    <>
      <h3
        style={{
          marginBottom: '20px',
          color: STRIPE_TEXT_PRIMARY,
          fontWeight: 'bold',
          fontSize: '28px',
        }}
      >
        Update a subscription
      </h3>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: '8px', // Add some padding for the scrollbar
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <h6
            style={{
              color: '#000000',
              marginBottom: '12px',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            Customer
          </h6>
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f6f9fc',
              borderRadius: '4px',
            }}
          >
            {customerLoading ? (
              <div style={{ color: STRIPE_TEXT_SECONDARY, fontSize: '14px' }}>
                Loading customer details...
              </div>
            ) : customerData ? (
              <>
                <div style={{ color: STRIPE_TEXT_PRIMARY }}>
                  {customerData.name || 'N/A'}
                </div>
                <div style={{ color: STRIPE_TEXT_SECONDARY, fontSize: '12px' }}>
                  {customerData.email || 'N/A'}
                </div>
              </>
            ) : (
              <div style={{ color: STRIPE_TEXT_SECONDARY, fontSize: '14px' }}>
                No customer details available
              </div>
            )}
          </div>
        </div>

        {/* Subscription Details Section */}
        <div style={{ marginBottom: '24px' }}>
          <h6
            style={{
              color: '#000000',
              marginBottom: '12px',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            Subscription Details
          </h6>
          <div
            style={{
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e6ebf1',
            }}
          >
            <DurationSection
              createdAt={
                org.organization_subscription?.created_at?.toString() || ''
              }
            />

            <CurrentSubscriptionItems
              orgId={org.id}
              onSubscriptionItemsChange={onSubscriptionItemsChange}
              onResetBillingIntervalChange={onResetBillingIntervalChange}
              onProrationSettingsChange={onProrationSettingsChange}
              onCouponsChange={onCouponsChange}
              trialDays={trialDays}
            />

            <FreeTrialDays
              onTrialDaysChange={setTrialDays}
              initialTrialDays={trialDays}
              org={org}
            />

            <MetadataManager
              onMetadataChange={setMetadata}
              initialMetadata={metadata}
              lockedKeys={['sale_rep_id', 'sale_rep_name', 'hubspot_deal_link']}
            />
          </div>
        </div>

        {/* Payment Method Section */}
        <div style={{ marginBottom: '24px' }}>
          <h6
            style={{
              color: '#000000',
              marginBottom: '12px',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            Payment Method
          </h6>
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f6f9fc',
              borderRadius: '4px',
              border: 'none',
            }}
          >
            <PaymentMethodDisplay
              paymentMethods={paymentMethods}
              isLoading={paymentMethodLoading}
              error={paymentMethodError as string | null}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onPaymentMethodSelect={setSelectedPaymentMethodId}
              onAddNewCard={() => setShowAddCardModal(true)}
              onPaymentDueDaysChange={setPaymentDueDays}
              invoicePreview={invoicePreview}
            />
          </div>
        </div>

        {!!stripePromise && (
          <AddPaymentMethodModal
            show={showAddCardModal}
            onCancel={() => setShowAddCardModal(false)}
            onSuccess={(paymentMethodId) => {
              setShowAddCardModal(false)
            }}
            customerId={org.organization_subscription?.stripe_customer_id || ''}
            organizationId={org.id}
            name={customerData?.name}
            email={customerData?.email}
            stripePromise={stripePromise}
          />
        )}
      </div>
    </>
  )
}

export default SubscriptionConfiguration
